import { useQueryClient } from "@tanstack/react-query";
import { Redo2, Undo2 } from "lucide-react";
import { useEffect, type ReactNode } from "react";

import { cn } from "@/lib/cn";
import { redoLast, undoLast, useUndoState } from "@/lib/undo";

/** En un campo de texto, Ctrl+Z es del campo: no deshace cambios guardados. */
function isTyping(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/** Deshacer y rehacer lo último guardado. Ctrl+Z deshace; Ctrl+Y (o Ctrl+Shift+Z) rehace. */
export function UndoBar() {
  const client = useQueryClient();
  const { past, future, busy, last } = useUndoState();
  /**
   * Refresca lo del negocio, **no la sesión**.
   *
   * Sin el filtro, deshacer volvía a preguntar quién eres: `invalidateQueries()` a secas
   * alcanza a todas las consultas, y `/auth/me` es una de ellas. Si esa vuelve mal un
   * instante, el portero deja de ver sesión y **borra la aplicación entera** — un botón de
   * deshacer no puede echar a nadie. Es el mismo tamaño de consecuencia que tenía el menú de
   * cuenta al no encontrar un nombre: local en apariencia, global de verdad.
   */
  const refresh = () => client.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "usuario" });
  const nextUndo = past[past.length - 1];
  const nextRedo = future[future.length - 1];

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || isTyping(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === "z" && !event.shiftKey) {
        event.preventDefault();
        void undoLast(refresh);
      } else if (key === "y" || (key === "z" && event.shiftKey)) {
        event.preventDefault();
        void redoLast(refresh);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="flex items-center gap-3">
      <p aria-live="polite" className="max-w-80 truncate text-xs text-light-3">
        {last}
      </p>
      <div className="glass flex rounded-lg p-0.5">
        <UndoButton
          label={nextUndo ? `Deshacer: ${nextUndo.label}` : "Nada para deshacer"}
          shortcut="Ctrl+Z"
          disabled={!nextUndo || busy}
          onClick={() => void undoLast(refresh)}
        >
          <Undo2 aria-hidden className="size-4" />
          Deshacer
        </UndoButton>
        <UndoButton
          label={nextRedo ? `Rehacer: ${nextRedo.label}` : "Nada para rehacer"}
          shortcut="Ctrl+Y"
          disabled={!nextRedo || busy}
          onClick={() => void redoLast(refresh)}
        >
          <Redo2 aria-hidden className="size-4" />
          Rehacer
        </UndoButton>
      </div>
    </div>
  );
}

function UndoButton({
  label,
  shortcut,
  disabled,
  onClick,
  children,
}: {
  label: string;
  shortcut: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={`${label} (${shortcut})`}
      aria-label={label}
      aria-keyshortcuts={shortcut.replace("Ctrl", "Control")}
      className={cn(
        "inline-flex min-h-9 items-center gap-1.5 rounded-md px-3 text-xs font-semibold text-light transition-colors",
        "hover:bg-white/10 disabled:pointer-events-none disabled:text-light-3",
      )}
    >
      {children}
    </button>
  );
}
